import { AppPreview, Root } from "./Root.jsx";
import { AppErrorBoundary } from "./components/AppErrorBoundary.jsx";
import { PrivacyPage, TermsPage } from "./components/Legal.jsx";
import { PasscodeReset, PinReset } from "./components/Onboarding.jsx";
import { WizardPreview } from "./components/Wizard.jsx";
import { captureFirstTouch, installErrorHook, setMetricsHousehold, startMetrics, track } from "./lib/metrics.js";
// LB-145: the side-by-side CMS marker. Arms itself ONLY on ?cms=1 and returns immediately
// otherwise — see src/lib/cmsPreview.js for why that matters.
import { startCmsPreview } from "./lib/cmsPreview.js";

// W2.5 metrics boot lives HERE, not in Root — the W1.10 lesson: preview routes render
// OUTSIDE Root, so anything Root-mounted silently doesn't exist for them (that's how the
// preview wizard shipped without CMS content). Boot once per page load, before routing,
// so every surface — invite gate, legal pages, previews, the app — shares one session
// and one flush pipeline. Preview routes pin the code to "PREVIEW", which the worker
// drops server-side: demos and the Playwright suite exercise the full client path
// without ever polluting the real dataset.
// Order matters: startMetrics FIRST — its session check reads the last-activity stamp,
// and any track() call (captureFirstTouch fires first_touch) writes that stamp; the
// other way round, session_start would be suppressed on every landing with utm params.
startMetrics(null);
// Crash visibility on every surface (docs/MONITORING_SCOPE.md §4) — boots here with
// metrics for the same W1.10 reason: preview/legal routes render outside Root.
installErrorHook();
captureFirstTouch();
// DIAGNOSTIC (28 Aug) — "Learn & Earn kicks me back to Kid Mode", reported on the Vocab
// tab. `mode` in App.jsx is plain useState defaulting to "child", so EVERY reload lands a
// parent back in Kid Mode. Three things reload the app and they look identical from the
// outside: the pull-to-refresh gesture (App.jsx — listens on window, in both modes, and
// fires whenever a downward swipe starts at scrollY 0, which a long filtered word list
// hits constantly), the service-worker controllerchange after a real update
// (index.html), and index.html's >30-min bfcache pageshow. Each now stamps a reason
// before reloading; this reads it once on the next boot and reports it, so the Analytics
// tab answers which one is actually firing instead of us picking a favourite.
//
// Read-and-clear: the stamp must not outlive the boot it explains. sessionStorage on
// purpose — it survives the reload it describes and dies with the tab, same reasoning as
// the lb-hidden-at stamp it sits beside.
try {
  const reason = sessionStorage.getItem("lb-reload-reason");
  if (reason) {
    sessionStorage.removeItem("lb-reload-reason");
    // `kind`, not `reason` (LB-60, 31 Aug). The prop allowlist is the fixed 17-blob layout
    // in §5 of the scope doc, and `reason` is not in it — so this event would have been
    // allowlisted and STILL landed with its one meaningful field stripped, which is the
    // same bug one layer down. `kind` is the existing "which variant" slot, used the same
    // way by app_error and quick_action.
    track("app_reload", { kind: reason });
  }
} catch (e) {}
{
  const bootParams = new URLSearchParams(window.location.search);
  if (bootParams.get("preview") === "wizard" || bootParams.get("preview") === "app") {
    setMetricsHousehold("PREVIEW");
  }
}

try { startCmsPreview(); } catch { /* the CMS marker must never stop the app rendering */ }

// Every surface goes inside the boundary, not just the app: the preview and legal routes
// render OUTSIDE Root (the W1.10 lesson, above), so a boundary around Root alone would leave
// them exactly as exposed as the app used to be.
ReactDOM.createRoot(document.getElementById("root")).render(
  <AppErrorBoundary>{(() => {
    const params = new URLSearchParams(window.location.search);
    // LB-8: the legal pages live at /terms and /privacy, served complete by the worker. This
    // shell still renders them for those pathnames — and for the old ?page= forms, which the
    // worker 301s when it is reached — because the service worker answers a FAILED navigation
    // with the cached shell (sw.js, isAppShell), and a parent reading the policy offline must
    // still get the policy, not an empty app. The words are the same src/lib/legal-text.js.
    const page = params.get("page") || (window.location.pathname === "/terms" ? "terms" : window.location.pathname === "/privacy" ? "privacy" : null);
    if (page === "terms") return <TermsPage />;
    if (page === "privacy") return <PrivacyPage />;
    // Emailed passcode-recovery link: littlebankers.uk/?recover=CODE&token=... — routed
    // straight here, bypassing the invite gate and the normal Onboarding/Root flow
    // entirely, same treatment as the other special-purpose routes above. See
    // PasscodeReset in Onboarding.jsx and worker/handlers/recovery.js for the full flow.
    if (params.get("recover")) return <PasscodeReset />;
    // Emailed PIN-recovery link: littlebankers.uk/?recover-pin=CODE&token=... — same
    // bypass treatment as passcode recovery above. See PinReset in Onboarding.jsx and
    // worker/handlers/pin-recovery.js for the full flow.
    if (params.get("recover-pin")) return <PinReset />;
    if (params.get("preview") === "wizard") return <WizardPreview />;
    // LB-301: no CMS preview host here any more — /cms/preview serves this same app with a
    // draft-bundle flag (content.js reads it), and the side-by-side CMS uses snapshots.
    if (params.get("preview") === "app") return <AppPreview />;
    return <Root />;
  })()}</AppErrorBoundary>
);
